import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import logo from '../../../img/logobranco.png'; // Importando o logo

// Componente para o gráfico de fundo estilizado
const BackgroundChart: React.FC = () => (
  <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{ stopColor: 'rgba(29, 78, 216, 0.1)' }} />
          <stop offset="100%" style={{ stopColor: 'rgba(29, 78, 216, 0)' }} />
        </linearGradient>
      </defs>
      <path
        d="M0,150 Q200,50 400,120 T800,150 T1200,100 T1600,180 L1600,0 L0,0 Z"
        fill="url(#chartGradient)"
        transform="translate(0, 200) scale(1.5)"
      />
      <path
        d="M-200,200 Q0,250 200,180 T600,220 T1000,160 T1400,250 T1800,200"
        stroke="#36ca9b"
        strokeWidth="3"
        fill="none"
        strokeDasharray="10 5"
        opacity="0.2"
        transform="translate(0, 50)"
      />
    </svg>
  </div>
);


const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();

  const handleSignupClick = () => {
    navigate('/signup'); 
  };

  const handleLoginClick = async () => {
    try {
      await auth.signinRedirect();
    } catch (error) {
      console.error('Erro ao redirecionar para login:', error);
    }
  };

  return (
    <div className="bg-accent text-dark font-sans relative">
      <BackgroundChart />
      <div className="relative z-10">
        {/* Header */}
        <header className="bg-primary sticky top-0 z-50 shadow-lg">
          <div className="container mx-auto px-6 py-8">
            <div className="flex justify-between items-center">
              <img src={logo} alt="SinalInvest Logo" className="h-16" style={{ filter: 'brightness(0) invert(1)' }} />
              
              <nav className="hidden md:flex items-center gap-8 text-base font-medium text-gray-200">
                <a href="#features" className="hover:text-white transition-colors">Funcionalidades</a>
                <a href="#how-it-works" className="hover:text-white transition-colors">Como Funciona</a>
                <a href="#testimonials" className="hover:text-white transition-colors">Depoimentos</a>
                <a onClick={() => navigate('/precos')} className="cursor-pointer hover:text-white transition-colors">Preços</a>
              </nav>
              
              <div className="hidden md:flex items-center gap-6">
                <button onClick={handleLoginClick} className="font-semibold text-gray-200 hover:text-white transition-all">
                  Entrar
                </button>
                <button onClick={handleSignupClick} className="px-6 py-2.5 rounded-lg font-bold text-dark bg-secondary hover:opacity-90 transition-all shadow-md">
                  Começar Grátis
                </button>
              </div>
              <div className="md:hidden">
                <button className="text-white">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" /></svg>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <main>
          <section className="min-h-screen flex items-center pt-16 pb-20">
            <div className="container mx-auto px-6 text-center">
              <h1 className="text-4xl md:text-6xl font-extrabold text-dark leading-tight mb-6">
                Notícias viram insights. <br /> Decisões viram <span className="text-primary">lucro</span>.
              </h1>
              <p className="max-w-3xl mx-auto text-lg md:text-xl text-gray-600 mb-10">
                Receba análises instantâneas de notícias por IA e saiba como o mercado vai reagir, 
                diretamente no seu WhatsApp. Antecipe-se. Invista com inteligência.
              </p>
              <div className="flex justify-center gap-4">
                <button onClick={handleSignupClick} className="px-8 py-3 rounded-lg font-bold text-white bg-primary hover:scale-105 transition-transform shadow-lg">
                  Criar minha conta
                </button>
                <a href="#features" className="px-8 py-3 rounded-lg font-bold text-primary bg-white border-2 border-gray-300 hover:border-primary transition-all">
                  Saber Mais
                </a>
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section id="features" className="py-24 bg-white">
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-extrabold text-dark">Uma nova forma de ler o mercado</h2>
                <p className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto">Ferramentas desenhadas para te dar a vantagem que você precisa, com a simplicidade que você quer.</p>
              </div>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200/50 flex flex-col text-left">
                  <div className="bg-primary/10 p-3 rounded-lg w-min mb-4">
                    <p className="text-3xl text-primary">🤖</p>
                  </div>
                  <h3 className="text-xl font-bold mb-2">IA Analista</h3>
                  <p className="text-gray-600">Nossa IA processa milhares de notícias e identifica o impacto para suas ações antes de todo mundo.</p>
                </div>
                <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200/50 flex flex-col text-left">
                  <div className="bg-secondary/10 p-3 rounded-lg w-min mb-4">
                    <p className="text-3xl text-secondary">⚡</p>
                  </div>
                  <h3 className="text-xl font-bold mb-2">Alertas no WhatsApp</h3>
                  <p className="text-gray-600">Insights direto no seu celular. Sem apps, sem logins complicados. Apenas a informação que importa.</p>
                </div>
                <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200/50 flex flex-col text-left">
                  <div className="bg-primary/10 p-3 rounded-lg w-min mb-4">
                    <p className="text-3xl text-primary">🎯</p>
                  </div>
                  <h3 className="text-xl font-bold mb-2">Monitoramento Customizado</h3>
                  <p className="text-gray-600">Escolha as empresas que você segue e receba apenas as análises que são relevantes para seu portfólio.</p>
                </div>
              </div>
            </div>
          </section>

          {/* How it Works Section */}
          <section id="how-it-works" className="py-24">
            <div className="container mx-auto px-6">
              <div className="grid md:grid-cols-2 gap-16 items-center">
                <div>
                  <h2 className="text-4xl font-extrabold text-dark mb-6">Simples como uma conversa.</h2>
                  <ul className="space-y-8">
                    <li className="flex items-start">
                      <div className="bg-primary text-white font-bold rounded-lg w-10 h-10 flex-shrink-0 flex items-center justify-center mr-4 text-lg">1</div>
                      <div>
                        <h3 className="font-bold text-xl">Cadastre-se e Escolha</h3>
                        <p className="text-gray-600">Crie sua conta e monte sua watchlist de empresas para monitorar.</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-secondary text-dark font-bold rounded-lg w-10 h-10 flex-shrink-0 flex items-center justify-center mr-4 text-lg">2</div>
                      <div>
                        <h3 className="font-bold text-xl">A IA Entra em Ação</h3>
                        <p className="text-gray-600">Nossos algoritmos monitoram o mercado 24/7 em busca de notícias.</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-primary text-white font-bold rounded-lg w-10 h-10 flex-shrink-0 flex items-center justify-center mr-4 text-lg">3</div>
                      <div>
                        <h3 className="font-bold text-xl">Receba o "Sinal"</h3>
                        <p className="text-gray-600">Você recebe um resumo claro e o veredito da IA diretamente no seu WhatsApp.</p>
                      </div>
                    </li>
                  </ul>
                </div>
                <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                    <div className="text-center">
                      <p className="text-6xl mb-4">📲</p>
                      <h3 className="text-2xl font-bold text-dark">Seu WhatsApp, <br/>Seu Novo Terminal Financeiro.</h3>
                      <p className="text-gray-600 mt-2">Simples, rápido e direto ao ponto.</p>
                    </div>
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials Section */}
          <section id="testimonials" className="py-24 bg-white">
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-extrabold text-dark">Milhares de investidores já confiam</h2>
              </div>
              <div className="grid lg:grid-cols-3 gap-8">
                {[
                  { name: 'Juliana S.', role: 'Investidora Swing Trader' },
                  { name: 'Marcos P.', role: 'Day Trader' },
                  { name: 'Fernanda L.', role: 'Investidora Longo Prazo' }
                ].map((testimonial) => (
                  <div key={testimonial.name} className="bg-accent/50 p-8 rounded-2xl">
                    <p className="text-gray-600 mb-6 font-medium">"Isso é um placeholder. A agilidade em receber a análise no WhatsApp é um diferencial enorme."</p>
                    <div className="flex items-center">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary mr-4"></div>
                      <div>
                        <p className="font-bold text-dark">{testimonial.name}</p>
                        <p className="text-sm text-gray-500">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-primary text-white">
            <div className="container mx-auto px-6 py-20 text-center">
              <h2 className="text-4xl font-extrabold mb-4">Pronto para investir com mais segurança?</h2>
              <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">Junte-se a milhares de investidores que tomam decisões mais rápidas e inteligentes.</p>
              <button onClick={handleSignupClick} className="px-10 py-4 rounded-lg font-bold text-primary bg-white hover:scale-105 transition-transform shadow-lg">
                Começar meu teste grátis
              </button>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-dark text-white">
          <div className="container mx-auto px-6 py-12">
            <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
               <div className="mb-8 md:mb-0">
                 <img src={logo} alt="SinalInvest Logo" className="h-10 mb-4 mx-auto md:mx-0" style={{ filter: 'brightness(0) invert(1)' }}/>
                 <p className="text-gray-400">Notícias viram lucro.</p>
               </div>
              <div className="flex gap-6 font-medium text-gray-300">
                <a href="#features" className="hover:text-secondary transition-colors">Funcionalidades</a>
                <a onClick={() => navigate('/precos')} className="cursor-pointer hover:text-secondary transition-colors">Preços</a>
                <a href="#" className="hover:text-secondary transition-colors">Termos</a>
                <a href="#" className="hover:text-secondary transition-colors">Privacidade</a>
              </div>
            </div>
            <div className="text-center text-gray-500 mt-10 pt-8 border-t border-gray-700">
              <p>&copy; {new Date().getFullYear()} SinalInvest. Todos os direitos reservados.</p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LandingPage;